import React from 'react';
import { X, Bus, Wrench, Shield } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Bus as BusType } from '../../types';

interface BusDetailsModalProps {
  bus: BusType;
  onClose: () => void;
}

export const BusDetailsModal: React.FC<BusDetailsModalProps> = ({ bus, onClose }) => {
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Not set';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const getStatusBadge = () => {
    if (!bus.isActive) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          Inactive
        </span>
      );
    }

    if (!bus.isInService) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
          Maintenance
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        In Service
      </span>
    );
  };

  const getFuelTypeBadge = () => {
    const colors = {
      electric: 'bg-purple-100 text-purple-800',
      diesel: 'bg-gray-100 text-gray-800',
      hybrid: 'bg-teal-100 text-teal-800',
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors[bus.fuelType as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {bus.fuelType ? bus.fuelType.charAt(0).toUpperCase() + bus.fuelType.slice(1) : 'Unknown'}
      </span>
    );
  };

  const groupedAmenities = (bus.amenities || []).reduce((acc, amenity) => {
    if (amenity && amenity.category) {
      if (!acc[amenity.category]) {
        acc[amenity.category] = [];
      }
      acc[amenity.category].push(amenity);
    }
    return acc;
  }, {} as Record<string, typeof bus.amenities>);

  const groupedFeatures = (bus.features || []).reduce((acc, feature) => {
    if (feature && feature.category) {
      if (!acc[feature.category]) {
        acc[feature.category] = [];
      }
      acc[feature.category].push(feature);
    }
    return acc;
  }, {} as Record<string, typeof bus.features>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Bus className="h-6 w-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-semibold text-secondary-900">
                Bus Details - {bus.busNumber}
              </h2>
            </div>
            <Button variant="outline" onClick={onClose} className="p-2">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Status and Basic Info */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Bus Number</label>
                    <p className="text-sm text-secondary-900">{bus.busNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Registration Number</label>
                    <p className="text-sm text-secondary-900">{bus.registrationNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Operator</label>
                    <p className="text-sm text-secondary-900">{bus.operatorName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Bus Type</label>
                    <p className="text-sm text-secondary-900">{bus.busType?.name || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Year of Manufacture</label>
                    <p className="text-sm text-secondary-900">{bus.yearOfManufacture}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Status</label>
                    <div className="mt-1">{getStatusBadge()}</div>
                  </div>
                </div>
              </Card>

              {/* Capacity and Fuel */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Capacity & Fuel</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Total Seats</label>
                    <p className="text-sm text-secondary-900">{bus.totalSeats}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Available Seats</label>
                    <p className="text-sm text-secondary-900">{bus.availableSeats}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Fuel Type</label>
                    <div className="mt-1">{getFuelTypeBadge()}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Mileage</label>
                    <p className="text-sm text-secondary-900">{bus.mileage?.toLocaleString() || 'N/A'} km</p>
                  </div>
                </div>

                {/* Seat Occupancy Bar */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-secondary-600 mb-2">Seat Occupancy</label>
                  <div className="w-full bg-secondary-200 rounded-full h-3">
                    <div
                      className="bg-primary-600 h-3 rounded-full"
                      style={{
                        width: `${((bus.totalSeats - bus.availableSeats) / bus.totalSeats) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-secondary-500 mt-1">
                    {bus.totalSeats - bus.availableSeats} of {bus.totalSeats} seats occupied
                  </p>
                </div>
              </Card>

              {/* Amenities */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Amenities</h3>
                {Object.keys(groupedAmenities).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(groupedAmenities).map(([category, categoryAmenities]) => (
                      <div key={category}>
                        <h4 className="text-sm font-medium text-secondary-600 mb-2 capitalize">
                          {category}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {categoryAmenities.map(amenity => (
                            <span
                              key={amenity.id}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                            >
                              {amenity.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-secondary-500">No amenities configured</p>
                )}
              </Card>

              {/* Features */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Features</h3>
                {Object.keys(groupedFeatures).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
                      <div key={category}>
                        <h4 className="text-sm font-medium text-secondary-600 mb-2 capitalize">
                          {category}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {categoryFeatures.map(feature => (
                            <span
                              key={feature.id}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-secondary-100 text-secondary-800"
                            >
                              {feature.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-secondary-500">No features configured</p>
                )}
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Maintenance Info */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center">
                  <Wrench className="h-5 w-5 mr-2" />
                  Maintenance
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Last Maintenance</label>
                    <p className="text-sm text-secondary-900">{formatDate(bus.lastMaintenanceDate)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Next Maintenance</label>
                    <p className="text-sm text-secondary-900">{formatDate(bus.nextMaintenanceDate)}</p>
                  </div>
                </div>
              </Card>

              {/* Insurance Info */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  Insurance
                </h3>
                <div>
                  <label className="block text-sm font-medium text-secondary-600">Expiry Date</label>
                  <p className="text-sm text-secondary-900">{formatDate(bus.insuranceExpiry)}</p>
                </div>
              </Card>

              {/* Bus Type Details */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Bus Type Details</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Type</label>
                    <p className="text-sm text-secondary-900">{bus.busType?.name || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Description</label>
                    <p className="text-sm text-secondary-900">{bus.busType?.description || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Base Price Multiplier</label>
                    <p className="text-sm text-secondary-900">{bus.busType?.basePriceMultiplier || 1}x</p>
                  </div>
                </div>
              </Card>

              {/* Timestamps */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">Timestamps</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Created</label>
                    <p className="text-sm text-secondary-900">
                      {new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }).format(bus.createdAt)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-600">Last Updated</label>
                    <p className="text-sm text-secondary-900">
                      {new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }).format(bus.updatedAt)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-secondary-200 mt-6">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
